import { Colors } from "@/enums/Colors";
import { Box, Button, CircularProgress, Dialog, IconButton, Typography, styled } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { CloseIcon } from "../utils/icons/CloseIcon";

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "1200px",
    width: "90vw",
    height: "85vh",
    margin: "auto",
    borderRadius: "12px",
    [theme.breakpoints.down("md")]: {
      width: "95vw",
      height: "90vh",
      maxWidth: "100%",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100vw",
      height: "100vh",
      maxHeight: "100vh",
      margin: 0,
      borderRadius: 0,
    },
  },
}));

export const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px 24px",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
  },
}));

export const ModalContent = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "calc(100% - 73px)", // Subtract header height
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

export const FileViewerSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  padding: "24px",
  overflow: "auto",
  [theme.breakpoints.down("sm")]: {
    padding: "12px",
  },
}));

export const ActionsSection = styled(Box)(({ theme }) => ({
  width: "350px",
  borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  overflowY: "auto",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    borderLeft: "none",
    borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    padding: "16px",
    gap: "16px",
    maxHeight: "200px",
  },
}));

export const ActionCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

export const ActionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: Colors.BLACK,
  marginBottom: "8px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
    marginBottom: "4px",
  },
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  padding: "12px 16px",
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "flex-start",
  textDecoration: "none",
  "&.MuiButton-contained": {
    backgroundColor: Colors.LAZO_PURPLE,
    color: Colors.WHITE,
    "&:hover": {
      backgroundColor: Colors.DARK_LAZO_PURPLE,
    },
  },
  "&.MuiButton-outlined": {
    borderColor: Colors.LIGHT_GRAY_DEDEE1,
    color: Colors.BLACK,
    "&:hover": {
      borderColor: Colors.LAZO_PURPLE,
      backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 14px",
    fontSize: "13px",
  },
}));

export const FilePreviewContainer = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: Colors.WHITE,
  borderRadius: "8px",
  overflow: "hidden",
  "& > div": {
    width: "100%",
    height: "100%",
  },
  "& iframe, & object": {
    width: "100% !important",
    height: "100% !important",
  },
});

export const LoadingContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  height: "100%",
});

export const InfoItem = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 600,
  color: Colors.LIGHT_GRAY_8A8E93,
  textTransform: "uppercase",
  [theme.breakpoints.down("sm")]: {
    fontSize: "11px",
  },
}));

export const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: Colors.BLACK,
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
}));

export const DescriptionBox = styled(Box)({
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  borderRadius: "8px",
  padding: "12px",
  minHeight: "60px",
});

export const DescriptionText = styled(Typography)({
  fontSize: "13px",
  color: Colors.LIGHT_GRAY_8A8E93,
  lineHeight: "1.5",
});

interface BaseDocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  headerContent?: ReactNode;
  filePreviewContent: ReactNode;
  actionsContent: ReactNode;
}

export function BaseDocumentViewerModal({
  open,
  onClose,
  title,
  headerContent,
  filePreviewContent,
  actionsContent,
}: BaseDocumentViewerModalProps) {
  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false}>
      <ModalHeader>
        <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
          {headerContent || (
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ width: "20px", height: "20px", fill: Colors.BLACK }} />
        </IconButton>
      </ModalHeader>

      <ModalContent>
        <FileViewerSection>{filePreviewContent}</FileViewerSection>
        <ActionsSection>{actionsContent}</ActionsSection>
      </ModalContent>
    </StyledDialog>
  );
}

// Utility hook for handling file URLs and PDF blobs
export function useFilePreview(fileExternalId: string | undefined, fileType: string, getPreSignedUrl: (id: string) => Promise<string>) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);

  useEffect(() => {
    if (fileExternalId) {
      setLoadingUrl(true);
      getPreSignedUrl(fileExternalId)
        .then((url) => {
          setFileUrl(url);

          // For PDFs, fetch and create blob URL for better rendering
          if (fileType === "pdf") {
            fetch(url)
              .then((r) => r.arrayBuffer())
              .then((pdf) => {
                const pdfBlob = new Blob([new Uint8Array(pdf)], {
                  type: "application/pdf",
                });
                const objectURL = URL.createObjectURL(pdfBlob);
                setPdfBlobUrl(objectURL);
              })
              .catch((error) => console.error("Error creating PDF blob:", error));
          }
        })
        .catch((error) => console.error("Error loading file URL:", error))
        .finally(() => setLoadingUrl(false));
    }

    return () => {
      setFileUrl(null);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
    };
  }, [fileExternalId, fileType]);

  return { fileUrl, pdfBlobUrl, loadingUrl };
}

interface FilePreviewLoadingProps {
  message?: string;
}

export function FilePreviewLoading({ message = "Loading file preview..." }: FilePreviewLoadingProps) {
  return (
    <LoadingContainer>
      <CircularProgress size={40} />
      <Typography color={Colors.LIGHT_GRAY_8A8E93}>{message}</Typography>
    </LoadingContainer>
  );
}
